import { buildAssetDetails } from "../../utils/asset-attribution";
import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { ArtworkUploadModal } from "./ArtworkUploadModal";
import configs from "../../utils/configs";
import { getSignedUrl } from "../../utils/artwork-utils";
import { guessContentType } from "../../utils/media-url-utils";
import { generateImageFileThumbnail, generateVideoFileThumbnail } from "../../utils/thumbnails";
import ThumbnailRenderer from "../../utils/thumbnail-renderer";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { RoomLayout } from "../layout/RoomLayout";
import styles from "./ObjectUrlModal.scss";
import {
  createBoundingBoxFromObject,
  convertDimensionsInchesToMeters,
  getArtworkInfo,
  getModelOnly,
  updateScale,
  updateScale2,
  convertDimensionsMetersToInches
} from "../../utils/artwork-model-utils";
import { DEFAULT_MEDIUM_URL } from "../../utils/artwork-model-utils";
import { fetchMediums } from "../../utils/artwork-utils";
import { getAuthHeaders } from "../../utils/getToken";
import { uploadClaimFields } from "../../utils/upload-claim";

const appName = configs.translation("app-name").toLowerCase().replace(/ /g, "-");

let loadingObject = null;

try {
  loadingObject = require(`../../assets/models/LoadingObject_${appName}.glb`);
} catch (e) {
  loadingObject = require("../../assets/models/LoadingObject_app.glb");
}

const isMobile = AFRAME.utils.device.isMobile() || AFRAME.utils.device.isMobileVR();

async function generateFileThumbnail(url, width, height, type) {
  const thumbnailRenderer = new ThumbnailRenderer();
  let blob;

  const loader = new GLTFLoader();
  const { scene } = await loader.loadAsync(url);
  blob = await thumbnailRenderer.generateThumbnail(scene);
  // }
  return blob;
}

async function generateAndUploadThumbnailUrl({ url, folder, token, name }) {
  const type = guessContentType(url);

  const thumbnailBlob = await generateFileThumbnail(url, folder, token, type);

  const thumbnailFile = new File([thumbnailBlob], `${name.split(".").shift()}-thumb.png`, { type: "image/png" });

  const signedUrl = await getSignedUrl({
    file: thumbnailFile,
    token,
    folder
  });

  await fetch(signedUrl.presigned_url, {
    method: "PUT",
    headers: {
      "Content-Type": "image/png",
      accept: "application/json",
      "x-ms-blob-type": "BlockBlob"
    },
    body: thumbnailFile
  });

  return { url: signedUrl.download_url, signedUpload: signedUrl };
}

export function ArtworkFormUploadContainer({ scene, onClose, sourceNavigate, open, artwork }) {
  const [mediums, setMedium] = useState([]);
  const [sizes, setSizes] = useState(null);
  const [initialTags, setInitialTags] = useState([]);

  useEffect(() => {
    const assetId = artwork?.el?.components["artwork"]?.data?.artworkId;
    if (!open || !assetId) return;
    let cancelled = false;
    const assetUrl = `${configs.PERSISTENCE_API_URL}/assets/${assetId}`;
    (async () => {
      try {
        const response = await fetch(assetUrl, { headers: await getAuthHeaders(assetUrl, "GET") });
        if (!response.ok) return;
        const body = await response.json();
        if (!cancelled) setInitialTags((body.data || body).tags || []);
      } catch (error) {
        console.error("Failed to load artwork tags:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, artwork]);

  const setMediums = async () => {
    try {
      let data = await fetchMediums();
      if (data && data.length > 0) {
        data = [{ name: "Digital File" }, ...data];
      } else {
        data = [{ name: "Digital File" }];
      }
      setMedium(data);
    } catch (error) {
      console.error("Failed to fetch mediums:", error);
      setMedium([{ name: "Digital File" }]);
    }
  };

  const [errorUrl, setErrorUrl] = useState(false);
  const onCleanUrl = () => {
    setErrorUrl(false);
  };

  const getSizes = async () => {
    let x, y, z;
    if (artwork.el.components["media-image"]) {
      const scale = artwork.el.components["media-image"]?.mesh.scale;
      const { xI, yI, zI } = convertDimensionsMetersToInches(scale.x, scale.y, scale.z);
      x = xI;
      y = yI;
      z = zI;
    } else {
      const scale = artwork.el.object3DMap.mesh.scale;
      const { xI, yI, zI } = convertDimensionsMetersToInches(scale.x, scale.y, scale.z);
      x = (xI * 1.5).toFixed(2);
      y = (yI * 1.5).toFixed(2);
      z = (zI * 1.5).toFixed(2);
    }

    setSizes({ x, y, z });
  };

  useEffect(() => {
    setMediums();
    getSizes();
  }, []);

  const onSubmit = useCallback(
    async ({
      artistName,
      artistStatement,
      date,
      description,
      dimensionsX,
      dimensionsY,
      dimensionsZ,
      name,
      medium,
      tags
    }) => {
      if (!name) {
        alert("Please enter an artwork title");
        return;
      }

      if (!artistName) {
        alert("Please enter an artist name");
        return;
      }
      let unit = "in";

      let dimensions = "";

      if (dimensionsY && dimensionsX) {
        // Both Width and Height are provided
        dimensions = `${dimensionsY} x ${dimensionsX} ${unit}`;
      } else if (dimensionsY || dimensionsX) {
        // Only one of Width or Height is provided
        alert("Please fill in both Width and Height.");
        return;
      } else {
        // No dimensions are provided
        dimensions = "";
      }

      const glbUrl = artwork.el?.components["gltf-model-plus"]?.lastSrc;

      // Trigger QR code generation
      artwork.el.emit("load-qr");

      // Wait for QR code to be generated and capture the qrCodeId
      const qrCodeId = await new Promise(resolve => {
        const handleQRLoaded = () => {
          const qrComponent = artwork.el.querySelector("[artwork-qr]");
          const generatedQrCodeId = qrComponent?.components["artwork-qr"]?.qrCodeId || "";
          artwork.el.removeEventListener("loaded-qr", handleQRLoaded);
          resolve(generatedQrCodeId);
        };

        artwork.el.addEventListener("loaded-qr", handleQRLoaded);

        // Fallback timeout in case QR generation fails
        setTimeout(() => {
          artwork.el.removeEventListener("loaded-qr", handleQRLoaded);
          resolve("");
        }, 10000);
      });

      const folder = `assets360/${configs.APP_NAME}/my-media`;

      const artworkInfo = getArtworkInfo({
        title: name,
        artworkDate: date,
        author: artistName,
        description,
        dimensions,
        dimensionsX,
        dimensionsY,
        dimensionsZ,
        artistStatement,
        medium
      });

      let scaleX,
        scaleY,
        scaleZ = 1;
      if (dimensions) {
        const { xM, yM, zM } = convertDimensionsInchesToMeters(dimensionsX, dimensionsY, dimensionsZ);
        (scaleX = xM), (scaleY = yM);
        scaleZ = zM;
      }

      let myMediaId = artwork.el.components["artwork"]?.data?.myMediaId;
      // upload artwork
      let artworkId = artwork.el.components["artwork"]?.data.artworkId;
      const artworkData = {
        artist: artistName,
        date,
        dimensions: dimensions,
        dimensionsX,
        dimensionsY,
        dimensionsZ,
        medium: medium,
        artist_statement: artistStatement,
        // artwork_model: glbUrl,
        asset_type: "my-media",
        unit: "in",
        qrCodeId: qrCodeId
      };
        if (!artworkId) {
          const thumbnailEvidence = artwork.el?.components["media-image"]
            ? { url: artwork.el.components["media-image"]?.data.src, signedUpload: null }
            : await generateAndUploadThumbnailUrl({ url: glbUrl, folder, name });
          const thumbnail = thumbnailEvidence?.url ?? null;
        // Upload Artwork to My Media
        const assetData = {
          title: name,
          description: description,
          type: artwork.el.components["media-image"] ? "image" : "model",
          path: artwork.el.components["media-image"] ? artwork.el.components["media-image"]?.data.src : glbUrl,
          asset_type: "my-media",
          url: artwork.el.components["media-image"] ? artwork.el.components["media-image"]?.data.src : glbUrl,
          thumbnail,
          public_thumbnail: thumbnail,
          ...uploadClaimFields(null, thumbnailEvidence?.signedUpload),
          // Include metadata with qrCodeId
          ...buildAssetDetails({
            assetCategory: "digital-twin-art",
            metadata: { artwork: { ...artworkInfo, qrCodeId } },
            tags: typeof tags === "string" ? tags.split(",") : []
          })
          // isHub: true
        };
        const myMediaUrl = `${configs["PERSISTENCE_API_URL"]}/assets`;

        // send to my media
        const response = await fetch(myMediaUrl, {
          method: "POST",
            headers: {
              "Content-Type": "application/json",
              accept: "application/json",
              ...(await getAuthHeaders(myMediaUrl, "POST"))
            },
          body: JSON.stringify({ ...assetData, ...artworkData, source: "metaverse" })
        });

        const myMedia = await response.json();
        artworkId = myMedia?.data?.id;
      }

      if (artwork.el.components["artwork"]?.data.artworkId) {
        const assetUrl = `${configs.PERSISTENCE_API_URL}/assets/${artworkId}`;
        const currentResponse = await fetch(assetUrl, { headers: await getAuthHeaders(assetUrl, "GET") });
        if (!currentResponse.ok) throw new Error("Failed to load artwork metadata");
        const currentBody = await currentResponse.json();
        const currentAsset = currentBody.data || currentBody;
        const response = await fetch(assetUrl, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", ...(await getAuthHeaders(assetUrl, "PATCH")) },
          body: JSON.stringify(
            buildAssetDetails({
              metadata: currentAsset.metadata || {},
              assetCategory: "digital-twin-art",
              tags: typeof tags === "string" ? tags.split(",") : currentAsset.tags || []
            })
          )
        });
        if (!response.ok) throw new Error("Failed to save artwork tags");
      }

      // show artwork in metaverse
      if (artwork.el.components["media-image"]) {
        let imageUrl = artwork.el.components["media-image"]?.data.src;
        if (!dimensions) {
          const textureLoader = new THREE.TextureLoader();

          const texture = await textureLoader.loadAsync(imageUrl);
          const textureWidth = texture.image.width;
          const textureHeight = texture.image.height;

          const aspectRatio = textureWidth / textureHeight;

          // Set the scaling factor for x and y based on the aspect ratio
          if (aspectRatio > 1) {
            scaleX = aspectRatio;
            scaleY = 1;
          } else {
            scaleX = 1;
            scaleY = 1 / aspectRatio;
          }
        }
        const mediumUrl = mediums.find(m => m.name === medium)?.model;

        artwork.el.setAttribute("artwork", {
          myMediaId: artworkId,
          glbModel: medium !== "Digital File" ? mediumUrl : null,
          image: imageUrl,
          completed: false,
          isDigitalFile: false,
          isArtwork: true,
          scaleX,
          scaleY,
          scaleZ,
          artworkInfo,
          isDigitalFile: medium == "Digital File" || !mediumUrl
        });
        // artwork.el.components['artwork']?.loadArtwork();

        // artwork.el.addEventListener("loaded-qr", function () {
        //   window.APP.pinningHelper.setPinned(artwork.el, true);
        // });
      } else {
        const mesh = artwork.el.object3DMap.mesh;
        const initialScaleX = artwork.el.components["artwork"]
          ? artwork.el.components["artwork"]?.data.initialScaleX
          : mesh.scale.x;
        const initialScaleY = artwork.el.components["artwork"]
          ? artwork.el.components["artwork"]?.data.initialScaleY
          : mesh.scale.y;
        const initialScaleZ = artwork.el.components["artwork"]
          ? artwork.el.components["artwork"]?.data.initialScaleZ
          : mesh.scale.z;
        const { scaleCoefficientX, scaleCoefficientY, scaleCoefficientZ } = updateScale2(
          artwork.el,
          scaleX,
          scaleY,
          scaleZ,
          initialScaleX,
          initialScaleY,
          initialScaleZ
        );
        artwork.el.object3D.visible = false;
        artwork.el.removeAttribute("artwork");

        artwork.el.setAttribute("artwork", {
          myMediaId: artworkId,
          glbModel: glbUrl,
          completed: false,
          isDigitalFile: false,
          artworkInfo,
          scaleX,
          scaleY,
          scaleZ,
          resizeOnInit: true,
          initialScaleX,
          initialScaleY,
          initialScaleZ,
          scaleCoefficientX,
          scaleCoefficientY,
          scaleCoefficientZ,
          myMediaId
        });
        artwork.el.addEventListener("loaded-qr", function () {
          artwork.el.object3D.visible = true;
          window.APP.pinningHelper.setPinned(artwork.el, true);
        });
      }

      onClose();
    },
    [scene, onClose]
  );

  console.log("artwork", artwork);

  return open ? (
    <RoomLayout
      className={styles.absolute}
      modal={
        <ArtworkUploadModal
          sizes={sizes}
          initialTags={initialTags}
          mediums={mediums}
          artwork={artwork}
          errorUrl={errorUrl}
          isMobile={isMobile}
          showModelCollectionLink={configs.feature("show_model_collection_link")}
          modelCollectionUrl={configs.link("model_collection", "https://sketchfab.com/mozillareality")}
          onSubmit={onSubmit}
          onClose={onClose}
          onCleanUrl={onCleanUrl}
          sourceNavigate={sourceNavigate}
          isImage={artwork?.el?.components["media-image"]}
        />
      }
    />
  ) : (
    <></>
  );
}

ArtworkFormUploadContainer.propTypes = {
  artwork: PropTypes.object,
  open: PropTypes.bool,
  scene: PropTypes.object.isRequired,
  onClose: PropTypes.func
};
